// Shared detection + recovery for stale-chunk failures after a deploy (version
// skew). Used by both the window-level guard (unhandled errors) and the React
// error boundary (handled errors that surface through Next's error.tsx).

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\d]+ failed/i,
  /Loading CSS chunk/i,
  // Next.js / Turbopack runtime wording, e.g. "Failed to load chunk
  // /_next/static/chunks/<hash>.js from module 788253".
  /Failed to load chunk/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

export function isChunkLoadError(value: unknown): boolean {
  if (!value) return false;
  const err = value as { name?: string; message?: string };
  const text = `${err.name ?? ""} ${err.message ?? ""}`;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(text));
}

const RELOAD_FLAG = "chunk-reload-at";
// Only auto-reload once per window — if the new deployment is genuinely broken
// (not just version-skewed) this stops an infinite reload loop.
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Returns true if a reload should proceed (and records it), false if we just
 * reloaded for a chunk error and are inside the cooldown.
 */
export function shouldReloadForChunkError(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
  } catch {
    // sessionStorage may be unavailable (private mode); proceed without it.
  }
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* ignore */
  }
  return true;
}
